import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import type { ReactNode } from 'react';

import { useAuthStore } from '@/stores/auth-store';
import type { TipId } from '@/tips';
import { cn } from '@/utils/cn';

import { Tip } from './tip';

/**
 * The top of every page.
 *
 * `headerActions` is the only place a page-level primary action lives. Page toolbars
 * are the one exception to Trinity's right-aligned-actions rule, and confining that
 * exception to this component is what keeps it from spreading.
 */

export interface Breadcrumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Optional catalog tip as a help icon beside the title. Page purpose stays in `description`. */
  tipId?: TipId;
  breadcrumbs?: Breadcrumb[];
  /** Rendered on the right. The page's primary action goes here and nowhere else. */
  headerActions?: ReactNode;
  /** Sits under the description — a status badge, a plan notice. */
  meta?: ReactNode;
  className?: string;
}

export const PageHeader = ({
  title,
  description,
  tipId,
  breadcrumbs,
  headerActions,
  meta,
  className,
}: PageHeaderProps) => {
  const hideActions = Boolean(useAuthStore((state) => state.user?.supportAccess));
  return (
  <header className={cn('mb-6 flex flex-col gap-3', className)}>
    {breadcrumbs && breadcrumbs.length > 0 ? (
      <nav>
        <ol className="flex flex-wrap items-center gap-1 text-caption text-fg-secondary">
          {breadcrumbs.map((crumb, index) => (
            <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
              {index > 0 ? (
                <ChevronRight aria-hidden className="h-3 w-3 text-fg-tertiary" />
              ) : null}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-fg hover:underline">
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-fg">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    ) : null}

    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="text-page-title font-semibold text-fg">{title}</h1>
          {tipId ? <Tip tipId={tipId} mode="icon" /> : null}
        </div>
        {description ? (
          <p className="mt-1 max-w-2xl text-body-sm text-fg-secondary">{description}</p>
        ) : null}
        {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
      </div>

      {headerActions && !hideActions ? (
        <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto">
          {headerActions}
        </div>
      ) : null}
    </div>
  </header>
  );
};
